import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { Link } from "react-router";
import { ordersApi } from "../services/ordersApi";
import { PaginationBar } from "@/components/ui/PaginationBar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const formatDate = (iso?: string) => {
  if (!iso) return "-";
  try {
    return format(parseISO(iso), "dd MMM yyyy HH:mm");
  } catch {
    return "-";
  }
};

export const OrderAmendmentHistoryPage = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const historyQuery = useQuery({
    queryKey: ["order-amendments", "history", pageNumber, pageSize],
    queryFn: () => ordersApi.getOrderAmendmentHistory(pageNumber, pageSize),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const rows = historyQuery.data?.items ?? [];

  if (historyQuery.isLoading) {
    return <div className="p-6">Loading order amendment history...</div>;
  }

  if (historyQuery.error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-600">Failed to load amendment history</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Order Amendment History</h1>
          <p className="text-muted-foreground mt-1">
            Full amendment timeline across orders.
          </p>
        </div>
        <Button variant="outline" onClick={() => historyQuery.refetch()}>
          Refresh
        </Button>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-lg border bg-muted/40 p-8 text-center text-muted-foreground">
          No order amendments found.
        </div>
      ) : (
        <div className="rounded-lg border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Amendment #</TableHead>
                <TableHead>Order #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Old Total</TableHead>
                <TableHead className="text-right">New Total</TableHead>
                <TableHead className="text-right">Delta</TableHead>
                <TableHead>Overpayment Handling</TableHead>
                <TableHead>Financial Action</TableHead>
                <TableHead>Requested At</TableHead>
                <TableHead>Approved At</TableHead>
                <TableHead>Applied At</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-mono text-xs">{row.amendmentNumber}</TableCell>
                  <TableCell className="font-mono text-xs">{row.orderNumber}</TableCell>
                  <TableCell>{row.customerName}</TableCell>
                  <TableCell><Badge variant="outline">{row.status}</Badge></TableCell>
                  <TableCell className="text-right">{row.oldOrderTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                  <TableCell className="text-right">{row.newOrderTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                  <TableCell className="text-right">{row.deltaAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                  <TableCell>
                    {row.financialAction && row.overpaymentDisposition ? (
                      <Badge variant="secondary">{row.overpaymentDisposition}</Badge>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    {row.financialAction ? (
                      <div className="space-y-1">
                        <div className="text-sm font-medium">{row.financialAction}</div>
                        <div className="flex flex-wrap gap-1">
                          {row.financialActionStatus && (
                            <Badge variant="outline">{row.financialActionStatus}</Badge>
                          )}
                          {row.financialReference && (
                            <Badge variant="outline" className="font-mono text-[10px]">{row.financialReference}</Badge>
                          )}
                        </div>
                        {row.financialActionQueue === "Payments Pending Approval" && (
                          <Link to="/payments?view=pending-approval&type=refund" className="text-xs text-primary underline underline-offset-2">
                            Open refund approvals
                          </Link>
                        )}
                        {row.financialActionQueue === "Wallet Approvals" && (
                          <Link to="/finance/wallet-approvals?type=Refund" className="text-xs text-primary underline underline-offset-2">
                            Open wallet approvals
                          </Link>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">No downstream action</span>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(row.requestedAt)}</TableCell>
                  <TableCell>{formatDate(row.approvedAt)}</TableCell>
                  <TableCell>{formatDate(row.appliedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {historyQuery.data && historyQuery.data.totalCount > 0 && (
        <PaginationBar
          currentPage={historyQuery.data.currentPage}
          pageSize={historyQuery.data.pageSize}
          totalCount={historyQuery.data.totalCount}
          totalPages={historyQuery.data.totalPages}
          onPageChange={setPageNumber}
          onPageSizeChange={(size) => { setPageSize(size); setPageNumber(1); }}
        />
      )}
    </div>
  );
};
